(() => {
  const WALK = 'walk-explore-set';
  const CALM = 'calm-feeding-set';
  const HARNESS = 'soft-no-pull-cat-harness-leash-set-comfortable-adjustable-walking-vest-for-cats-small-dogs';
  const ADDON = '3m-retractable-pet-leash';
  const cache = new Map();

  const currentHandle = () => {
    const match = location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const productJson = async (handle) => {
    if (cache.has(handle)) return cache.get(handle);
    const request = fetch(`/products/${handle}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error(`Unable to load ${handle}`);
      return response.json();
    });
    cache.set(handle, request);
    return request;
  };

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    return `$${value.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2
    })}`;
  };

  const syncHomeCard = async (handle, title) => {
    const product = await productJson(handle);
    const cards = [...document.querySelectorAll('.powlife-bundle-card')];
    const card = cards.find((item) => {
      const href = item.querySelector('a[href]')?.getAttribute('href') || '';
      const heading = item.querySelector('h3')?.textContent?.trim() || '';
      return href.includes(`/products/${handle}`) || heading === title;
    });
    if (!card) return;

    const row = card.querySelector('[data-bundle-pricing]');
    const sale = row?.querySelector('[data-bundle-sale]');
    const compare = row?.querySelector('[data-bundle-compare]');
    const save = row?.querySelector('[data-bundle-save]');
    if (!row || !sale) return;

    const price = Number(product.price || 0);
    const comparePrice = Number(product.compare_at_price || 0);
    sale.textContent = money(price);

    if (compare && save && comparePrice > price) {
      compare.hidden = false;
      save.hidden = false;
      compare.textContent = money(comparePrice);
      save.textContent = `Save ${money(comparePrice - price)}`;
    }
  };

  const syncHome = () => {
    syncHomeCard(WALK, 'Walk & Explore Set').catch(() => {});
    syncHomeCard(CALM, 'Calm Meals & Clean Hydration Set').catch(() => {});
  };

  const normalizeLbLabels = () => {
    const handle = currentHandle();
    if (handle !== WALK && handle !== HARNESS) return;
    const root = document.querySelector('[data-powlife-product]');
    if (!root) return;

    const targets = root.querySelectorAll('.pl-option__button, [data-option-label]');
    targets.forEach((element) => {
      [...element.childNodes].forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.nodeValue && node.nodeValue.includes('Lb')) {
          node.nodeValue = node.nodeValue.replace(/Lb/g, 'lb');
        }
      });
    });
  };

  const selectedParentVariant = (root, product) => {
    const form = root.querySelector('form[action*="/cart/add"]');
    const id = Number(form?.querySelector('[name="id"]')?.value || root.querySelector('[data-variant-select]')?.value || 0);
    return product.variants?.find((variant) => Number(variant.id) === id)
      || product.variants?.find((variant) => variant.available)
      || product.variants?.[0]
      || null;
  };

  const firstAvailableVariant = (product) => product.variants?.find((variant) => variant.available)
    || product.variants?.[0]
    || null;

  const setVisiblePrice = (root, text) => {
    const current = root.querySelector('[data-product-current-price]');
    if (current) {
      current.textContent = text;
      return;
    }
    const price = root.querySelector('[data-product-price]');
    if (price) price.textContent = text;
  };

  const updateWalkPrice = async () => {
    if (currentHandle() !== WALK) return;
    const root = document.querySelector('[data-powlife-product]');
    if (!root) return;

    const [walkProduct, addonProduct] = await Promise.all([productJson(WALK), productJson(ADDON)]);
    const parent = selectedParentVariant(root, walkProduct);
    const addon = firstAvailableVariant(addonProduct);
    if (!parent || !addon) return;

    const checkbox = root.querySelector('[data-addon-choice]');
    const total = Number(parent.price || 0) + (checkbox?.checked ? Number(addon.price || 0) : 0);
    setVisiblePrice(root, money(total));

    const addonPrice = root.querySelector('.pl-addon__price');
    const addonNote = root.querySelector('.pl-addon__note');
    if (addonPrice) addonPrice.textContent = money(addon.price);
    if (addonNote) addonNote.textContent = `Optional add-on • +${money(addon.price)}`;
  };

  const schedulePrice = () => {
    requestAnimationFrame(() => {
      updateWalkPrice().catch(() => {});
      setTimeout(() => updateWalkPrice().catch(() => {}), 80);
      setTimeout(() => updateWalkPrice().catch(() => {}), 220);
    });
  };

  const bind = () => {
    if (currentHandle() !== WALK) return;
    const root = document.querySelector('[data-powlife-product]');
    if (!root || root.dataset.walkParentPriceSync === 'true') return;
    root.dataset.walkParentPriceSync = 'true';

    root.addEventListener('change', schedulePrice, true);
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-option-value], [data-walk-value], [data-addon-choice]')) schedulePrice();
    }, true);

    document.addEventListener('submit', async (event) => {
      if (currentHandle() !== WALK) return;
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form || !root.contains(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submit = form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;

      try {
        const [walkProduct, addonProduct] = await Promise.all([productJson(WALK), productJson(ADDON)]);
        const parent = selectedParentVariant(root, walkProduct);
        const addon = firstAvailableVariant(addonProduct);
        const checkbox = root.querySelector('[data-addon-choice]');
        const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));
        if (!parent) throw new Error('Parent variant unavailable');

        const items = [{ id: Number(parent.id), quantity }];
        if (checkbox?.checked) {
          if (!addon) throw new Error('Add-on unavailable');
          items.push({ id: Number(addon.id), quantity });
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Unable to add bundle');
        window.location.href = window.routes?.cart_url || '/cart';
      } catch (error) {
        console.error('[Walk bundle price sync]', error);
        if (submit) submit.disabled = false;
      }
    }, true);

    schedulePrice();
  };

  const boot = () => {
    syncHome();
    bind();
    normalizeLbLabels();
    setTimeout(normalizeLbLabels, 120);
    setTimeout(normalizeLbLabels, 360);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('click', (event) => {
    if (event.target.closest('.pl-option__button')) setTimeout(normalizeLbLabels, 0);
  }, true);
})();
